export type CadenceKey =
  | "daily"
  | "weekly"
  | "biweekly"
  | "monthly"
  | "quarterly";

/** Card-stack display order, top (highest frequency) to bottom — design-note.md §3.2 */
export const CADENCE_ORDER: CadenceKey[] = [
  "daily",
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
];

/**
 * Cadence badge colors — sourced from the prototype's actual CSS
 * (guidance-hub-prototype.html .cadence-pill.* classes), which the build plan
 * names as the source of truth for CSS conventions. Note this deliberately
 * differs from design-note.md §3.2's color table (daily/weekly are swapped
 * there, and quarterly is amber, not red, in the real prototype).
 */
export const CADENCE_BADGE_STYLES: Record<
  CadenceKey,
  { bg: string; text: string; border: string }
> = {
  daily: { bg: "#eff4ff", text: "#1d4ed8", border: "#bfcefe" },
  weekly: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0" },
  biweekly: { bg: "#fdf4ff", text: "#7c3aed", border: "#e9d5ff" },
  monthly: { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  quarterly: { bg: "#fef9c3", text: "#854d0e", border: "#fde68a" },
};

/** Emoji + solid accent color per cadence — sourced from the prototype's
 * CADENCE_META (icon + color), used for card icons, the color bar, the
 * floating card, and CTA button backgrounds. */
export const CADENCE_ICON: Record<CadenceKey, string> = {
  daily: "☀️",
  weekly: "📅",
  biweekly: "🔄",
  monthly: "📊",
  quarterly: "🏁",
};

export const CADENCE_SOLID_COLOR: Record<CadenceKey, string> = {
  daily: "#2563eb",
  weekly: "#16a34a",
  biweekly: "#7c3aed",
  monthly: "#ea580c",
  quarterly: "#ca8a04",
};

/** Generic, role-agnostic cadence names — the prototype's global
 * CADENCE_META.label, used for each card's own small badge (e.g. "DAILY").
 * Distinct from a role's admin-editable User tag (`cadence_tag` on the
 * playbook cell), which drives the Rhythm strip, quick-jump pills, and
 * Rhythm & Outcomes modal via resolveCadenceLabel(). */
export const CADENCE_GENERIC_LABEL: Record<CadenceKey, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

/** One admin-editable field (Playbook Content Editor's "User tag") can
 * override a cell's displayed cadence name — e.g. relabeling CXO's
 * `biweekly` cell as "Situational". Empty/null/whitespace-only falls back
 * to the generic name. */
export const resolveCadenceLabel = (
  cadenceTag: string | null | undefined,
  cadenceKey: CadenceKey
): string => cadenceTag?.trim() || CADENCE_GENERIC_LABEL[cadenceKey];

/** Day-of-week (0=Sun..6=Sat) -> cadence bucket, admin-editable via
 * Guidance Hub Admin > Org Settings > Cadence Schedule (backed by
 * vendor_config key "guidance_cadence_schedule"). A day not listed in any
 * bucket falls through to "daily". */
export interface CadenceSchedule {
  daily: number[];
  weekly: number[];
  monthly: number[];
  quarterly: number[];
}

export const DEFAULT_CADENCE_SCHEDULE: CadenceSchedule = {
  daily: [2, 3, 4], // Tue, Wed, Thu
  weekly: [0, 1, 6], // Sun, Mon, Sat
  monthly: [1], // Monday
  quarterly: [1], // Monday
};

/**
 * Day-of-week -> cadence surfacing logic (design-note.md §4.2 / §10.1.1).
 * Priority chain: Quarterly > Monthly > Weekly > Daily. Bi-weekly is never
 * auto-surfaced — it's tied to specific sponsor strategy calls, not a weekday.
 *
 * Note: monthly/quarterly also require being in the first week of the
 * month/quarter — a quarter that starts mid-week shows Weekly/Daily until
 * that month's matching schedule day. Intentional simplification;
 * calendar-aware surfacing is a later phase (design-note.md §8).
 *
 * `dayOverride` backs the Admin > Org Settings "Preview as day" simulator —
 * it swaps only the day-of-week used for matching, while the first-week-of-
 * month/quarter check still uses the real current date, UNLESS overridden by
 * `firstWeekOverride`/`firstMonthOverride` (the simulator's "1st week of
 * month" / "1st week of quarter" checkboxes) — matching the prototype's own
 * `previewFirstWeek || date <= 7` / `previewFirstQuarter || month % 3 === 0`
 * pattern, so the Monthly/Quarterly branches can be exercised from any real
 * calendar date, not just when it happens to actually be the first week of a
 * quarter-start month.
 */
export const getTodayCadence = (
  date: Date = new Date(),
  schedule: CadenceSchedule = DEFAULT_CADENCE_SCHEDULE,
  dayOverride?: number | null,
  firstWeekOverride?: boolean,
  firstMonthOverride?: boolean
): CadenceKey => {
  const dayOfWeek = dayOverride ?? date.getDay(); // 0 = Sunday, 6 = Saturday
  const dateOfMonth = date.getDate();
  const month = date.getMonth(); // 0-11
  const isFirstWeekOfMonth = firstWeekOverride || dateOfMonth <= 7;
  const isQuarterStartMonth =
    firstMonthOverride || [0, 3, 6, 9].includes(month); // Jan/Apr/Jul/Oct

  if (
    isFirstWeekOfMonth &&
    isQuarterStartMonth &&
    schedule.quarterly.includes(dayOfWeek)
  ) {
    return "quarterly";
  }
  if (isFirstWeekOfMonth && schedule.monthly.includes(dayOfWeek)) {
    return "monthly";
  }
  if (schedule.weekly.includes(dayOfWeek)) {
    return "weekly";
  }
  return "daily";
};

/** Which cadence bucket a day currently falls in — for rendering the 7-row
 * Cadence Schedule table in Admin > Org Settings' "Primary cadence" column.
 *
 * Checks buckets in daily/weekly/monthly/quarterly order — matching the
 * reference prototype's `Object.entries(CADENCE_SCHEDULE)` declaration-order
 * scan exactly, not getTodayCadence's runtime resolution priority
 * (quarterly > monthly > weekly > daily). The two intentionally differ: a
 * day can sit in `weekly` *and* `monthly`/`quarterly` at once (e.g. the
 * default schedule's Monday, in all three) — `monthly`/`quarterly`
 * membership there is a conditional, first-week-only override shown via the
 * Notes column, not a competing claim on that day's everyday "primary"
 * cadence, which is what this function reports. */
export const getCadenceForDay = (
  schedule: CadenceSchedule,
  day: number
): CadenceKey => {
  for (const cadence of ["daily", "weekly", "monthly", "quarterly"] as const) {
    if (schedule[cadence].includes(day)) return cadence;
  }
  return "daily";
};

/** Rebuilds a full schedule object from 7 per-day cadence picks (the Org
 * Settings table's dropdowns) — inverse of getCadenceForDay per-day. */
export const buildScheduleFromDayAssignments = (
  assignments: Record<number, Exclude<CadenceKey, "biweekly">>
): CadenceSchedule => {
  const schedule: CadenceSchedule = {
    daily: [],
    weekly: [],
    monthly: [],
    quarterly: [],
  };
  for (let day = 0; day <= 6; day++) {
    const cadence = assignments[day] || "daily";
    schedule[cadence].push(day);
  }
  return schedule;
};
